import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import {
    ACADEMY_COURSE_DATA_KEY_PREFIX,
    ACADEMY_TRAININGS_LISTING_KEY_PREFIX,
    ACADEMY_TRAININGS_MANUAL_KEY_PREFIX,
    ACADEMY_TRAININGS_RELATED_KEY_PREFIX,
    ACADEMY_TRAININGS_SINGLE_KEY_PREFIX
} from '~/utils/academy/constants';
import log from '~/utils/log';
import type { NuxtPayload } from '#app';

/**
 * Whether a loaded listing page shows the course, so a refresh reaches the pages displaying it and
 * leaves the rest alone. Matched on `courseId` rather than `id`, which an event can carry too.
 */
const containsCourse = (pageData: unknown, courseId: string | number): boolean => {
    const items = (pageData as AcademyTrainingsListingResponse | null)?.items;

    if (!Array.isArray(items)) {
        return false;
    }

    return items.some((item) => item.type === 'course' && String(item.courseId) === String(courseId));
};

export const _extractKeysToRefresh = (payloadData: NuxtPayload['data'], courseId: string | number): string[] => {
    return Object.entries(payloadData)
        .filter(([key, pageData]) => {
            if (!key.startsWith(ACADEMY_COURSE_DATA_KEY_PREFIX)) {
                return false;
            }

            if (
                key.startsWith(ACADEMY_TRAININGS_LISTING_KEY_PREFIX) ||
                key.startsWith(ACADEMY_TRAININGS_RELATED_KEY_PREFIX) ||
                key.startsWith(ACADEMY_TRAININGS_MANUAL_KEY_PREFIX)
            ) {
                return containsCourse(pageData, courseId);
            }

            if (key === `${ACADEMY_TRAININGS_SINGLE_KEY_PREFIX}:${courseId}`) {
                return true;
            }

            return false;
        })
        .map(([key]) => key);
};

/**
 * Marks a course stale and re-runs the fetches showing it, for after the player was closed.
 *
 * Everything carrying course status shares one key prefix. The detail and its related trainings are
 * refetched outright; the listing is spread over one entry per loaded page, so those are narrowed by
 * content to the pages actually holding the course.
 */
export const useAcademyCourseRefresh = () => {
    return async (courseId: string | number) => {
        // Not conditional on the marking: a timed-out request does not say whether it landed.
        await invalidateAcademyCourseCache(courseId);

        const nuxtApp = useNuxtApp();
        const keys = _extractKeysToRefresh(nuxtApp.payload.data, courseId);

        if (!keys.length) {
            return;
        }

        try {
            await refreshNuxtData(keys);
        } catch (error) {
            log.warn('[academy] failed to refresh the page data', { courseId, error });
        }
    };
};
